import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import "./weeklyReport.css";
import toast from "react-hot-toast";
import axios from "axios";
import Swal from "sweetalert2";
import "sweetalert2/dist/sweetalert2.min.css"; // optional, for default styling

import { FileText, Users, MessageSquare, BookOpen } from "lucide-react";
import WeeklyReportForm from "../../../components/lecturer/reportForm/reportForm";

const WeeklyReport = () => {
  const API_ENDPOINT = process.env.REACT_APP_API_END_POINT;
  const nav = useNavigate();
  const steps = [
    { number: 1, title: "Module & Dates", icon: BookOpen },
    { number: 2, title: "Weekly Report", icon: FileText },
    { number: 3, title: "Communication", icon: MessageSquare },
    { number: 4, title: "Sessions", icon: Users },
  ];
  const [currentStep, setCurrentStep] = useState(1);
  const [showReportPreview, setShowReportPreview] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [modules, setModules] = useState([]);
  const [selectedModule, setSelectedModule] = useState({});
  const [groups, setGroups] = useState([]);
  const [channels, setChannels] = useState([]);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [sessionFiles, setSessionFiles] = useState([]);

  const [reportData, setReportData] = useState({
    moduleIds: [],
    startDate: "",
    endDate: "",
    suggestions: "",
    challenges: "",
    channelActivities: [],
    weeklyActivity: "",
    sessions: [],
  });

  const getModules = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/api/LecturerAcademy/GetMyModules`,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );
      console.log("Modules API response:", response.data);
      const mods = Array.isArray(response?.data?.modules)
        ? response.data.modules
        : [];
      setModules(mods);
      if (!response?.data?.status) {
        toast.error(response.data?.message || "Failed to load modules");
      }
    } catch (error) {
      console.error("Error fetching modules:", error);
      toast.error(error.response?.data?.message || "An error occurred");
    }
  }, [API_ENDPOINT]);

  const getCommunicationChannel = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_ENDPOINT}/api/AcademyGet/GetAllCommunicationChannels`,
        {
          withCredentials: true,
          headers: { "Content-Type": "application/json" },
        },
      );
      console.log("Channels API response:", response.data);
      if (response?.data?.status) {
        setChannels(response.data.channels);
      } else {
        toast.error(response.data.message || "Failed to load channels");
      }
    } catch (error) {
      console.error("Error fetching channels:", error);
      toast.error(error.response?.data?.message || "An error occurred");
    }
  }, [API_ENDPOINT]);
  useEffect(() => {
    getModules();
    getCommunicationChannel();
  }, [getModules, getCommunicationChannel]);

  const getModuleGroups = useCallback(
    async (moduleIds) => {
      try {
        const response = await axios.post(
          `${API_ENDPOINT}/api/LecturerAcademy/GetlecturerModuleGroups`,
          { moduleIds },
          {
            withCredentials: true,
            headers: { "Content-Type": "application/json" },
          },
        );
        if (response?.data?.status) {
          console.log(
            "This are the groups for the selected module" +
              " " +
              response?.data?.groups,
          );
          setGroups(response.data.groups);
        } else {
          console.log(response.data.message);
        }
      } catch (error) {
        console.error(error);
      }
    },
    [API_ENDPOINT],
  );

  // Load groups when modules are selected
  useEffect(() => {
    if (reportData.moduleIds && reportData.moduleIds.length > 0) {
      getModuleGroups(reportData.moduleIds);
    } else {
      setGroups([]);
    }
  }, [reportData.moduleIds, getModuleGroups]);

  //getStart and End date of the report
  function getCurrentWeekDates() {
    const today = new Date();
    const day = today.getDay();

    const monday = new Date(today);
    monday.setDate(today.getDate() - ((day + 6) % 7));

    const saturday = new Date(monday);
    saturday.setDate(monday.getDate() + 5);

    const format = (date) => date.toISOString().split("T")[0];

    return {
      monday: format(monday),
      saturday: format(saturday),
    };
  }

  //communication channels and activities
  const handleChannelSelection = (channelId) => {
    if (selectedChannels.includes(channelId)) {
      setSelectedChannels((prev) => prev.filter((id) => id !== channelId));
      setReportData((prev) => {
        const { [channelId]: _, ...remaining } = prev.channelActivities || {};
        return {
          ...prev,
          channelActivities: remaining,
        };
      });
    } else {
      setSelectedChannels((prev) => [...prev, channelId]);
      setReportData((prev) => ({
        ...prev,
        channelActivities: {
          ...prev.channelActivities,
          [channelId]: "",
        },
      }));
    }
  };

  const updateChannelActivity = (channelId, value) => {
    setReportData((prev) => ({
      ...prev,
      channelActivities: {
        ...prev.channelActivities,
        [channelId]: value,
      },
    }));
  };

  //handle sessions
  const addSession = () => {
    const newSession = {
      sessionNumber: 1,
      numberOfStudents: "",
      groupId: 0,
      sessionDate: "",
    };

    setReportData((prev) => ({
      ...prev,
      sessions: [...prev.sessions, newSession],
    }));
  };

  const updateSession = (index, field, value) => {
    setReportData((prev) => {
      const updatedSessions = [...prev.sessions];
      const sessionToUpdate = { ...updatedSessions[index], [field]: value };

      if (
        (field === "groupId" || field === "sessionDate") &&
        sessionToUpdate.groupId &&
        sessionToUpdate.sessionDate
      ) {
        const sameGroupSameDateSessions = updatedSessions.filter(
          (s, i) =>
            i !== index &&
            s.groupId === sessionToUpdate.groupId &&
            s.sessionDate === sessionToUpdate.sessionDate,
        );

        if (sameGroupSameDateSessions.length >= 1) {
          toast.error("A group can only have one session per day.");
          return prev;
        }
      }

      if (field === "groupId" && value !== 0) {
        const sameGroupSessions = updatedSessions.filter(
          (s, i) => s.groupId === value && i !== index,
        );

        sessionToUpdate.sessionNumber = sameGroupSessions.length + 1;
      }

      updatedSessions[index] = sessionToUpdate;

      return {
        ...prev,
        sessions: updatedSessions,
      };
    });
  };

  const removeSession = (index) => {
    setReportData((prev) => ({
      ...prev,
      sessions: prev.sessions.filter((_, i) => i !== index),
    }));
  };

  //report submition
  const submitReport = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);

    // Validate that attendance register is uploaded before submission
    if (sessionFiles.length === 0) {
      Swal.fire({
        icon: "error",
        title: "Attendance Register Required",
        text: "You cannot submit a report without uploading an attendance register. Please upload an attendance register file before submitting.",
        showConfirmButton: true,
        customClass: { popup: "my-swal-theme" },
      });
      return;
    }

    const channelActivities = selectedChannels
      .filter((channelId) => reportData.channelActivities[channelId]?.trim())
      .map((channelId) => {
        const channel = channels.find((c) => c.channelId === channelId);
        return {
          channelName: channel?.channelName || "Unknown",
          activity: reportData.channelActivities[channelId],
        };
      });

    try {
      const formData = new FormData();

      formData.append("startDate", reportData.startDate);
      formData.append("endDate", reportData.endDate);
      formData.append("suggestions", reportData.suggestions || "");
      formData.append("challenges", reportData.challenges || "");
      formData.append("weeklyActivity", reportData.weeklyActivity || "");
      formData.append("draftId", 0);

      reportData.moduleIds.forEach((id, index) => {
        formData.append(`moduleIds[${index}]`, id);
      });

      channelActivities.forEach((activity, index) => {
        formData.append(
          `channelActivities[${index}].channelName`,
          activity.channelName,
        );
        formData.append(
          `channelActivities[${index}].activity`,
          activity.activity,
        );
      });

      reportData.sessions.forEach((session, index) => {
        formData.append(
          `sessions[${index}].sessionNumber`,
          session.sessionNumber,
        );
        formData.append(
          `sessions[${index}].numberOfStudents`,
          session.numberOfStudents,
        );
        formData.append(`sessions[${index}].groupId`, session.groupId);
        formData.append(`sessions[${index}].sessionDate`, session.sessionDate);
      });

      sessionFiles.forEach((file) => {
        formData.append("files", file);
      });

      const uploadConfig = { withCredentials: true };
      const response = await axios.post(
        `${API_ENDPOINT}/api/LecturerReports/SubmitReport`,
        formData,
        {
          ...uploadConfig,
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        Swal.fire({
          icon: "success",
          title: "Success",
          text: response.data.message,
          showConfirmButton: true,
          customClass: { popup: "my-swal-theme" },
        });

        nav("/report-history");
      } else {
        setShowReportPreview(false);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: response.data.message || "Failed to submit report.",
          showConfirmButton: true,
          customClass: { popup: "my-swal-theme" },
        });
      }
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Failed!",
        text: error.response?.data?.message || "An unexpected error occurred.",
        showConfirmButton: true,
        customClass: { popup: "my-swal-theme" },
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  //Drafts
  const saveDraft = async (e) => {
    e.preventDefault();

    if (!reportData.moduleIds) {
      toast.error("Please select at least one module.");

      return;
    }

    const channelActivities = selectedChannels
      .filter((channelId) => reportData.channelActivities[channelId]?.trim())
      .map((channelId) => {
        const channel = channels.find((c) => c.channelId === channelId);
        return {
          channelName: channel?.channelName || "Unknown",
          activity: reportData.channelActivities[channelId],
        };
      });

    const formData = new FormData();

    formData.append("startDate", reportData.startDate);
    formData.append("endDate", reportData.endDate);
    formData.append("suggestions", reportData.suggestions || "");
    formData.append("challenges", reportData.challenges || "");
    formData.append("weeklyActivity", reportData.weeklyActivity || "");
    formData.append("draftId", 0);

    reportData.moduleIds.forEach((id, index) => {
      formData.append(`moduleIds[${index}]`, id);
    });

    channelActivities.forEach((activity, index) => {
      formData.append(
        `channelActivities[${index}].channelName`,
        activity.channelName,
      );
      formData.append(
        `channelActivities[${index}].activity`,
        activity.activity,
      );
    });

    reportData.sessions.forEach((session, index) => {
      formData.append(
        `sessions[${index}].sessionNumber`,
        session.sessionNumber,
      );
      formData.append(
        `sessions[${index}].numberOfStudents`,
        session.numberOfStudents,
      );
      formData.append(`sessions[${index}].groupId`, session.groupId);
      formData.append(`sessions[${index}].sessionDate`, session.sessionDate);
    });

    sessionFiles.forEach((file) => {
      formData.append("files", file);
    });

    try {
      const uploadConfig = { withCredentials: true };
      const response = await axios.post(
        `${API_ENDPOINT}/api/LecturerReports/CreateDraft`,
        formData,
        {
          ...uploadConfig,
          validateStatus: () => true,
        },
      );

      if (response?.data?.status) {
        toast.success(response.data.message || "Draft saved successfully!");
        nav("/lecturer-drafts");
      } else {
        toast.error(response.data.message || "Failed to save draft.");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Error saving draft.");
    }
  };

  //Navigation between the steps
  const canProceedToNext = () => {
    switch (currentStep) {
      case 1:
        return (
          reportData.moduleIds.length > 0 &&
          reportData.startDate &&
          reportData.endDate
        );
      case 2:
        return reportData.weeklyActivity.trim();
      case 3:
        return selectedChannels.every((channelId) =>
          reportData.channelActivities?.[channelId]?.trim(),
        );
      case 4:
        return (
          reportData.sessions.length > 0 &&
          reportData.sessions.every(
            (s) => s.groupId > 0 && s.numberOfStudents > 0 && s.sessionDate,
          ) &&
          sessionFiles.length !== 0
        );
      default:
        return false;
    }
  };

  const validateDates = (start, end) => {
    if (!start || !end) return false;

    const startDate = new Date(start);
    const endDate = new Date(end);

    // Rule 1: Not more than 7 days
    const diffDays = Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24));
    if (diffDays > 6) {
      toast.error("The report period cannot be more than 7 days.");
      return false;
    }

    // Rule 2: End date must be Saturday of the week of start date
    const dayOfWeek = startDate.getDay();
    const saturdayDate = new Date(startDate);
    saturdayDate.setDate(startDate.getDate() + (6 - dayOfWeek));

    if (endDate > saturdayDate) {
      toast.error(
        "End date cannot be later than the Saturday of the selected week.",
      );
      return false;
    }

    return true;
  };
  const handleNextStep = () => {
    if (currentStep === 1) {
      getModuleGroups(reportData.moduleIds);
      if (!validateDates(reportData.startDate, reportData.endDate)) return;
    }
    if (currentStep === 2) {
      if (!reportData.weeklyActivity.trim()) {
        toast.error("Please fill in the Weekly Activity before proceeding.");
        return;
      }
    }

    if (currentStep < 4) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  return (
    <div className="lecturer-Weekly-Report-page">
      <WeeklyReportForm
        reportType={"Create"}
        steps={steps}
        currentStep={currentStep}
        setCurrentStep={setCurrentStep}
        reportData={reportData}
        setReportData={setReportData}
        selectedModule={selectedModule}
        setSelectedModule={setSelectedModule}
        getCurrentWeekDates={getCurrentWeekDates}
        getModuleGroups={getModuleGroups}
        modules={modules}
        communicationChannels={channels}
        selectedChannels={selectedChannels}
        handleChannelSelection={handleChannelSelection}
        updateChannelActivity={updateChannelActivity}
        addSession={addSession}
        removeSession={removeSession}
        updateSession={updateSession}
        availableGroups={groups}
        canProceedToNext={canProceedToNext}
        handleNext={handleNextStep}
        sessionFiles={sessionFiles}
        setSessionFiles={setSessionFiles}
        setShowReportPreview={setShowReportPreview}
        showReportPreview={showReportPreview}
        submitReport={submitReport}
        isSubmitting={isSubmitting}
        saveDraft={saveDraft}
      />
    </div>
  );
};

export default WeeklyReport;
